import type { APIError } from '@vizit/api';
import { Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { Image01Icon } from '@vizit/icons';
import { LibraryEmptyState } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import React from 'react';
import type { PressEvent } from 'react-aria';
import { CollectionCard } from '../collection-card';
import { containerClasses, gridClasses, loadingCountClasses } from './styles';

export interface CollectionItem {
  /**
   * Unique identifier for the collection
   */
  id: string;
  /**
   * The title of the collection
   */
  title: string;
  /**
   * Number of items in the collection
   */
  itemCount: number;
  /**
   * Optional preview image URL (typically the thumbnail)
   */
  previewImageUrl?: string | null;
  /**
   * Optional fallback preview image URL used when the primary image fails to load
   */
  previewImageFallbackUrl?: string | null;
  /**
   * Badge text (e.g., category name)
   */
  badgeText: string;
  /**
   * Custom background color for the badge
   */
  badgeBackgroundColor?: string | null;
  /**
   * Custom border color for the badge
   */
  badgeBorderColor?: string | null;
}

export interface CollectionCardGridProps {
  /**
   * Array of collection items to display
   */
  collections: CollectionItem[];
  /**
   * Total number of collections (for display text)
   */
  totalCount: number;
  /**
   * Whether to show loading state
   */
  isLoading?: boolean;
  /**
   * Number of loading cards to display
   */
  loadingCount?: number;
  /**
   * Custom CSS class for the grid container
   */
  className?: string;
  /**
   * Custom text to display before the count (e.g., 'Showing', 'Found')
   */
  countPrefix?: string;
  /**
   * Callback when a collection card is clicked
   */
  onCollectionPress?: (id: string, event: PressEvent) => void;
  /**
   * Custom empty state content
   */
  emptyStateContent?: React.ReactNode;
  /**
   * Action button to display in the header (e.g., Create collection button)
   */
  headerAction?: React.ReactNode;
  /**
   * Portfolio ID for collection operations
   */
  portfolioId?: string;
  /**
   * Organization ID for collection operations
   */
  organizationId?: string;
  /**
   * Callback when a collection is renamed
   */
  onCollectionRename?: (collectionId: string, newName: string) => void;
  /**
   * Callback when a collection is deleted
   */
  onCollectionDelete?: (collectionId: string) => void;
  /**
   * Callback when an authentication error occurs
   */
  onAuthenticationError?: (error: APIError) => void;
}

const LoadingCollectionCardGrid = React.memo(
  ({ loadingCount = 8, className }: { loadingCount: number; className?: string }) => {
    return (
      <div className={cx(containerClasses(), className)}>
        <Skeleton variant="square" className={loadingCountClasses()} />
        <div className={gridClasses()}>
          {Array.from({ length: loadingCount }, (_, index) => {
            const skeletonId = `collection-skeleton-${index}`;
            return (
              <CollectionCard
                key={skeletonId}
                id={skeletonId}
                title="Loading..."
                itemCount={0}
                previewImageUrl={null}
                badgeText="Loading..."
                isLoading
              />
            );
          })}
        </div>
      </div>
    );
  },
);

const EmptyCollectionResult = React.memo(({ children }: React.PropsWithChildren) => {
  return (
    <LibraryEmptyState
      heading="No collections found"
      description="No collections match your search, or there are no collections yet."
      icon={Image01Icon}
    >
      {children}
    </LibraryEmptyState>
  );
});

export const CollectionCardGrid = React.forwardRef<HTMLDivElement, CollectionCardGridProps>(
  (
    {
      collections,
      totalCount,
      loadingCount = 8,
      className,
      countPrefix = '',
      isLoading = false,
      emptyStateContent,
      onCollectionPress,
      headerAction,
      portfolioId,
      organizationId,
      onCollectionRename,
      onCollectionDelete,
      onAuthenticationError,
      ...other
    },
    ref,
  ) => {
    const isLoadingCollectionGrid = isLoading && collections.length === 0;
    const isEmptyResult = collections.length === 0 && !isLoading;

    if (isEmptyResult) {
      return <EmptyCollectionResult>{emptyStateContent}</EmptyCollectionResult>;
    }

    if (isLoadingCollectionGrid) {
      return <LoadingCollectionCardGrid loadingCount={loadingCount} className={className} />;
    }

    return (
      <div ref={ref} className={cx(containerClasses(), className)} {...other}>
        <div className="flex items-center justify-between mb-6">
          <Text size={TextSize.Sm} color={TextColor.Tertiary} weight={TextWeight.Medium} showEllipsis={false}>
            Showing {totalCount} {totalCount === 1 ? 'collection' : 'collections'}
          </Text>
          {headerAction && <div>{headerAction}</div>}
        </div>

        <div
          aria-label={`Collection grid containing ${totalCount} ${totalCount === 1 ? 'collection' : 'collections'}`}
          className={gridClasses()}
        >
          {collections.map((collection) => (
            <CollectionCard
              key={collection.id}
              id={collection.id}
              title={collection.title}
              itemCount={collection.itemCount}
              previewImageUrl={collection.previewImageUrl}
              previewImageFallbackUrl={collection.previewImageFallbackUrl}
              badgeText={collection.badgeText}
              badgeBackgroundColor={collection.badgeBackgroundColor}
              badgeBorderColor={collection.badgeBorderColor}
              onPress={onCollectionPress}
              portfolioId={portfolioId}
              organizationId={organizationId}
              onRename={onCollectionRename}
              isLoading={isLoading}
              onDelete={onCollectionDelete}
              onAuthenticationError={onAuthenticationError}
            />
          ))}
        </div>
      </div>
    );
  },
);
